import React, { useState, useEffect, useRef } from 'react';
import './HeroVideo.css';
import HeroFallback from './HeroFallback';

// Responsive video URLs for different device sizes
const videoCDNUrls = {
  mobile: "https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video-480.webm",
  tablet: "https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video-720.webm", 
  desktop: "https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video-1080.webm",
  mp4: "https://images.vastrebyvijh.com/vastre-images/hero-video/hero-video.mp4"
};

// Function to get the appropriate video URL based on viewport
const getVideoUrlForViewport = () => {
  // Use a safe default if window is not available (SSR)
  if (typeof window === 'undefined') {
    return videoCDNUrls.mobile;
  }
  
  const width = window.innerWidth;
  
  if (width <= 480) {
    return videoCDNUrls.mobile;
  } else if (width <= 1024) {
    return videoCDNUrls.tablet;
  } else {
    return videoCDNUrls.desktop;
  }
};

const HeroVideo = () => {
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [showVideoContent, setShowVideoContent] = useState(true);
  const [videoLoadingProgress, setVideoLoadingProgress] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoUrl, setVideoUrl] = useState(() => getVideoUrlForViewport());
  const videoRef = useRef(null);

  // Handle responsive video URL changes on resize
  useEffect(() => {
    const handleResize = () => {
      const newUrl = getVideoUrlForViewport();
      if (newUrl !== videoUrl) {
        setVideoUrl(newUrl);
      }
    };

    // Debounce resize events
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 300);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimer);
    };
  }, [videoUrl]);

  // Video loading with progress tracking
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    // Event handlers for progressive loading
    const handleLoadedMetadata = () => {
      setVideoLoadingProgress(30);
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const duration = video.duration;
        if (duration > 0) {
          const progress = 30 + ((bufferedEnd / duration) * 60); // 30% to 90%
          setVideoLoadingProgress(Math.min(progress, 90));
        }
      }
    };

    const handleCanPlay = () => {
      setVideoLoadingProgress(100);
      setIsVideoLoading(false);
      setShowVideoContent(true);
      // Start playing immediately
      video.play().catch(() => {});
    };

    const handleError = () => {
      // Try fallback to MP4 if WebM fails
      if (videoUrl !== videoCDNUrls.mp4) {
        setVideoUrl(videoCDNUrls.mp4);
      } else {
        // If MP4 also fails, show fallback component
        setIsVideoLoading(false);
        setShowVideoContent(false);
        setVideoFailed(true);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // Start initial loading
    setVideoLoadingProgress(10);

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl]);

  // Fallback timer to hide loader if video takes too long
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (isVideoLoading) {
        setIsVideoLoading(false);
      }
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, [isVideoLoading]);

  return (
    <>
      {/* Show Fallback if Video Failed */}
      {videoFailed && <HeroFallback />}
      
      {/* Hero Section - Full Screen Video */}
      {!videoFailed && (
        <section className="herovideo-section" style={{ display: showVideoContent ? 'block' : 'none' }}>
          {/* Full Screen Video Background */}
          <div className="herovideo-container">
          <video
            ref={videoRef}
            key={videoUrl}
            className="herovideo-player"
            src={videoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
              if (!e.target) return;
              // Set playback rate to 0.7 (slower speed) for cinematic effect
              e.target.playbackRate = 0.7;
            }}
            onLoadedData={(e) => {
              if (!e.target) return;
              // Hide fallback layout when video loads successfully
              const heroSection = e.target.closest('.herovideo-section');
              if (heroSection) {
                heroSection.classList.remove('herovideo-fallback-mode');
                heroSection.classList.add('herovideo-loaded');
              }
            }}
            onCanPlay={(e) => {
              if (!e.target) return;
              // Video is ready to play
              e.target.play().catch(() => {});
            }}
          />

          {/* Fallback image */}
          <img
            src="https://images.vastrebyvijh.com/vastre-images/home-hero/desktop/2025-08-26/home-hero_desktop_1756239228357_0_1920w.webp"
            alt="Hero Image - Vastré By Vijh"
            className="herovideo-fallback-img"
            style={{ display: 'none' }}
            loading="eager"
            fetchpriority="high"
          />
        </div>

        {/* Scroll Indicator */}
        <div className="herovideo-scroll-indicator" onClick={() => {
          const nextSection = document.querySelector('.signature-section, .collections-section, .elegant-about-section');
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}>
          <div className="herovideo-scroll-arrow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 14L12 19L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 5L12 10L17 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="herovideo-scroll-text">Scroll to explore</span>
        </div>
        </section>
      )}
    </>
  );
};

export default HeroVideo;
